/**
 * iter85 - Admin Users tab (Item S).
 *
 * Search by email/name -> table of matching students with cascade preview
 * (host case) + destructive delete button. Delete opens a modal that
 * requires the admin to type the user's email verbatim before the DELETE
 * fires. On success, the user, case, and personal data are gone; Moments
 * they created transfer to a co-organizer or get a `creator_deleted`
 * marker; comments they wrote flip to "Deleted user".
 *
 * Also renders a compact audit log tail at the bottom so the admin
 * always sees who deleted what and when.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Trash2, Shield, AlertTriangle, X, UserCircle } from "lucide-react";
import UserDetailDrawer from "./UserDetailDrawer";

export default function AdminUsers() {
  const [q, setQ]           = useState("");
  const [results, setResults] = useState(null);
  const [target,  setTarget]  = useState(null);   // user to delete
  const [audit,   setAudit]   = useState(null);
  const [hygiene, setHygiene] = useState(null);
  const [openUser, setOpenUser] = useState(null); // {id} for detail drawer

  const search = async (needle) => {
    const query = (needle || "").trim();
    if (query.length < 2) { setResults(null); return; }
    try {
      const r = await api.get(`/admin/users?q=${encodeURIComponent(query)}`);
      setResults(r.data);
    } catch (e) {
      setResults({ error: e?.response?.data?.detail || "Search failed" });
    }
  };

  const loadAudit = async () => {
    try { setAudit((await api.get("/admin/actions?limit=20")).data); } catch { setAudit({ error: true }); }
  };
  const loadHygiene = async () => {
    try { setHygiene((await api.get("/admin/hygiene/last-run")).data); } catch { setHygiene({ error: true }); }
  };

  useEffect(() => { loadAudit(); loadHygiene(); }, []);

  return (
    <div className="mt-6 space-y-8" data-testid="admin-users-tab">
      {/* --- U.4 hygiene indicator ---------------------------------- */}
      <HygieneCard hygiene={hygiene}/>

      {/* --- Search bar --------------------------------------------- */}
      <section>
        <div className="text-xs uppercase tracking-widest text-elec font-bold">Users</div>
        <div className="mt-2 text-[13px] text-ink/70 max-w-2xl">
          Search students by email or name. Deletion is immediate on this
          admin path (no grace period). Cascade: user + case + personal data
          removed; Moments transfer to a co-organizer or stay visible with a
          "creator account deleted" marker; comments show as "Deleted user".
        </div>
        <div className="mt-4 flex items-center gap-2">
          <div className="relative flex-1 max-w-lg">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"/>
            <input
              data-testid="admin-users-search"
              type="text"
              value={q}
              onChange={(e) => { setQ(e.target.value); search(e.target.value); }}
              placeholder="Type at least 2 characters of email or name"
              className="moe-input pl-9 w-full"
            />
          </div>
        </div>

        {results?.error && (
          <div className="mt-4 moe-card p-4 text-sm text-[color:var(--moe-pink)]">{results.error}</div>
        )}
        {results?.users && (
          <div className="mt-4 moe-card overflow-hidden">
            <table className="w-full text-sm" data-testid="admin-users-results">
              <thead className="bg-black/5 text-[10px] uppercase tracking-widest text-muted">
                <tr>
                  <th className="text-left p-3">Student</th>
                  <th className="text-left p-3">Host</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {results.users.map(u => (
                  <tr key={u.id} className="border-t border-ink/10" data-testid={`admin-user-row-${u.email}`}>
                    <td className="p-3">
                      <div className="font-semibold text-ink">{u.name || "-"}</div>
                      <div className="text-[12px] text-muted">{u.email}</div>
                    </td>
                    <td className="p-3 text-[12px]">
                      {u.case
                        ? <>{u.case.host_university || "-"}<div className="text-muted">{u.case.host_city}, {u.case.country}</div></>
                        : <span className="text-muted italic">No case</span>}
                    </td>
                    <td className="p-3">
                      {u.deleted_at
                        ? <span className="moe-chip moe-chip-pink">Soft-deleted · purge {u.deletion_purge_at?.split("T")[0]}</span>
                        : u.suspended_at
                          ? <span className="moe-chip moe-chip-pink" data-testid={`admin-user-suspended-chip-${u.email}`}>Suspended</span>
                          : <span className="moe-chip moe-chip-mint">Active</span>}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          data-testid={`admin-detail-btn-${u.email}`}
                          onClick={() => setOpenUser({ id: u.id })}
                          className="moe-btn moe-btn-ghost !py-1.5 !px-3 text-xs">
                          <UserCircle size={12}/> Details
                        </button>
                        <button
                          data-testid={`admin-delete-btn-${u.email}`}
                          onClick={() => setTarget(u)}
                          className="moe-btn moe-btn-ghost !py-1.5 !px-3 text-xs text-[color:var(--moe-pink)]">
                          <Trash2 size={12}/> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {results.users.length === 0 && (
                  <tr><td colSpan={4} className="p-6 text-center text-muted italic text-sm">
                    {results.note || "No matches."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* --- Audit log tail ------------------------------------------ */}
      <section data-testid="admin-audit-log">
        <div className="text-xs uppercase tracking-widest text-elec font-bold flex items-center gap-2">
          <Shield size={12} strokeWidth={2.4}/> Audit log
        </div>
        <div className="mt-2 text-[12px] text-ink/70">
          Most recent admin destructive actions. Retained indefinitely.
        </div>
        {!audit ? <div className="mt-3 moe-shimmer h-20"/> :
          audit.error ? <div className="mt-3 moe-card p-4 text-sm text-muted italic">Couldn't load audit log.</div> :
          audit.actions?.length === 0 ? <div className="mt-3 moe-card p-4 text-sm text-muted italic">No admin actions recorded yet.</div> :
          <div className="mt-3 moe-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-black/5 text-[10px] uppercase tracking-widest text-muted">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Actor</th>
                  <th className="text-left p-3">Action</th>
                  <th className="text-left p-3">Target</th>
                </tr>
              </thead>
              <tbody>
                {audit.actions.map(a => (
                  <tr key={a.id} className="border-t border-ink/10">
                    <td className="p-3 text-[12px] whitespace-nowrap">{a.created_at?.split("T")[0]} {a.created_at?.split("T")[1]?.slice(0, 8)}</td>
                    <td className="p-3 text-[12px]">{a.actor_admin_email}</td>
                    <td className="p-3 text-[12px]"><b>{a.action_type}</b></td>
                    <td className="p-3 text-[12px]">{a.target_email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </section>

      {/* --- Delete confirmation modal ------------------------------- */}
      {target && (
        <DeleteConfirmModal
          user={target}
          onClose={() => setTarget(null)}
          onDeleted={() => { setTarget(null); search(q); loadAudit(); }}
        />
      )}

      {/* --- Detail drawer (RR + TT + UU) ---------------------------- */}
      {openUser && (
        <UserDetailDrawer
          userId={openUser.id}
          onClose={() => setOpenUser(null)}
          onChanged={() => { search(q); loadAudit(); }}
        />
      )}
    </div>
  );
}

function DeleteConfirmModal({ user, onClose, onDeleted }) {
  const [typed,   setTyped] = useState("");
  const [busy,    setBusy]  = useState(false);
  const [error,   setError] = useState(null);
  const match = typed.trim().toLowerCase() === (user.email || "").toLowerCase();

  const fire = async () => {
    if (!match) return;
    setBusy(true); setError(null);
    try {
      const r = await api.delete(`/admin/users/${user.id}`, {
        data: { confirm_email: typed.trim() },
      });
      const counts = r.data?.counts || {};
      const transferred = counts.moments_creator_transferred || 0;
      const orphaned    = counts.moments_creator_marked_deleted || 0;
      const commented   = counts.comments_author_masked || 0;
      toast.success(
        `Deleted ${user.email}. Moments: ${transferred} transferred, ${orphaned} preserved. ${commented} comments masked.`
      );
      onDeleted();
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="admin-delete-modal">
      <div className="moe-card max-w-lg w-full p-6 bg-paper relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-muted hover:text-ink"><X size={18}/></button>
        <div className="flex items-start gap-3">
          <AlertTriangle size={22} className="text-[color:var(--moe-pink)] shrink-0 mt-0.5"/>
          <div>
            <div className="text-lg font-bold">Delete {user.email}?</div>
            <div className="mt-1 text-[13px] text-ink/75 leading-snug">
              Immediate cascade. This user's account, case, tasks, documents,
              Cohort profile, connections, and messages will be removed.
              Moments they created transfer to a co-organizer where present;
              otherwise they stay visible with a "creator account deleted"
              marker. Comments they wrote flip to "Deleted user". This is
              logged in the audit trail with your admin identity.
            </div>
            <div className="mt-4 text-[11px] uppercase tracking-widest text-muted font-bold">Type the email to confirm</div>
            <input
              data-testid="admin-delete-confirm-input"
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={user.email}
              className="mt-1 moe-input w-full"
              autoFocus
            />
            {error && <div className="mt-2 text-sm text-[color:var(--moe-pink)]">{error}</div>}
            <div className="mt-5 flex items-center gap-3 justify-end">
              <button onClick={onClose} className="moe-btn moe-btn-ghost">Cancel</button>
              <button
                data-testid="admin-delete-fire"
                disabled={!match || busy}
                onClick={fire}
                className={`moe-btn ${match ? "moe-btn-primary" : "moe-btn-ghost"}`}
              >
                {busy ? "Deleting..." : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HygieneCard({ hygiene }) {
  if (!hygiene) return null;
  const run = hygiene.run;
  if (!run) {
    return (
      <div className="moe-card p-4 text-[12px] text-muted italic" data-testid="hygiene-card-empty">
        Database hygiene job hasn't run yet (24h cadence, first pass fires shortly after startup).
      </div>
    );
  }
  return (
    <div className="moe-panel-mint p-4 text-ink" data-testid="hygiene-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold">
        Last cleanup pass
      </div>
      <div className="mt-1 text-[13px]">
        <b>{run.finished_at?.replace("T", " ").split(".")[0]}</b> UTC ·
        <span className="ml-2">purged users: <b className="tabular-nums">{run.purged_users || 0}</b></span> ·
        <span className="ml-2">pruned page events: <b className="tabular-nums">{run.pruned_page_events || 0}</b></span>
        {run.errors?.length > 0 && <span className="ml-2 text-[color:var(--moe-pink)]">errors: {run.errors.length}</span>}
      </div>
    </div>
  );
}
